import React from 'react';
import { 
  Form, 
  Input, 
  Typography
} from 'antd';
import { 
  FileTextOutlined, 
  NumberOutlined,
  EditOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Details Step Component
 * Handles order number, internal case notes, and fault description
 */
const DetailsStep = ({ form, formData, updateFormData, loading }) => {

  /**
   * Handle order number change
   */
  const handleOrderNumberChange = (e) => {
    updateFormData({
      orderNumber: e.target.value
    });
  };

  /**
   * Handle internal case notes change
   */
  const handleInternalNotesChange = (e) => {
    updateFormData({
      internalCaseNotes: e.target.value
    });
  };

  /**
   * Handle fault description change
   */
  const handleDescriptionChange = (e) => {
    updateFormData({
      description: e.target.value
    });
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>
        <FileTextOutlined style={{ marginRight: '8px' }} />
        Order & Fault Details
      </Title>
      
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Provide order information and describe the breakdown fault
      </Text>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px'
      }}>
        <div style={{ maxWidth: '400px' }}>
          <Form.Item
            label="Order Number"
            name="orderNumber"
            help="Reference number for this breakdown request"
          >
            <Input
              placeholder="Enter order number"
              prefix={<NumberOutlined />}
              value={formData.orderNumber}
              onChange={handleOrderNumberChange}
            />
          </Form.Item>
        </div>

        <div style={{ width: '100%' }}>
          <Form.Item
            label="Internal Case Notes"
            name="internalCaseNotes"
            help="Internal notes for case management (not visible to customer)"
          >
            <TextArea
              placeholder="Enter any internal notes or comments about this case..."
              rows={4}
              value={formData.internalCaseNotes}
              onChange={handleInternalNotesChange}
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </div>

        <div style={{ width: '100%' }}>
          <Form.Item
            label="Describe the Fault"
            name="description"
            rules={[
              { required: true, message: 'Please describe the breakdown fault' },
              { min: 10, message: 'Fault description must be at least 10 characters' }
            ]}
          >
            <TextArea
              placeholder="Provide a detailed description of the breakdown or fault..."
              rows={6}
              value={formData.description}
              onChange={handleDescriptionChange}
              maxLength={2000}
              showCount
            />
          </Form.Item>
        </div>
      </div>

      {/* Summary of key information */}
      <div style={{ 
        background: '#fafafa', 
        border: '1px solid #d9d9d9', 
        borderRadius: '6px', 
        padding: '16px',
        marginTop: '24px'
      }}>
        <Title level={5} style={{ marginBottom: '12px' }}>
          <EditOutlined style={{ marginRight: '8px' }} />
          Request Summary
        </Title>
        
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px'
        }}>
          <div style={{ flex: '1', minWidth: '280px' }}>
            <Text strong>Controller:</Text>
            <Text style={{ display: 'block', marginBottom: '8px' }}>
              {formData.controllerName || 'Not specified'}
            </Text>
            
            <Text strong>Customer:</Text>
            <Text style={{ display: 'block', marginBottom: '8px' }}>
              {formData.selectedCompany?.name || 'Not selected'}
            </Text>
            
            <Text strong>Driver:</Text>
            <Text style={{ display: 'block', marginBottom: '8px' }}>
              {formData.driverName || 'Not specified'}
            </Text>
          </div>
          
          <div style={{ flex: '1', minWidth: '280px' }}>
            <Text strong>Vehicle:</Text>
            <Text style={{ display: 'block', marginBottom: '8px' }}>
              {formData.truckRegistrationNumber ? 
                `${formData.truckRegistrationNumber} (${formData.truckMake} ${formData.truckModel})` : 
                'Not specified'
              }
            </Text>
            
            <Text strong>Location:</Text>
            <Text style={{ display: 'block', marginBottom: '8px' }}>
              {formData.address || 'Not specified'}
            </Text>
            
            <Text strong>Trailers:</Text>
            <Text style={{ display: 'block' }}>
              {formData.trailer1Fault && formData.trailer2Fault ? 
                'Trailer 1 & 2 Faults' :
                formData.trailer1Fault ? 
                'Trailer 1 Fault' :
                formData.trailer2Fault ? 
                'Trailer 2 Fault' :
                'No trailer faults'
              }
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsStep;
